import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["selectedRows", "toggleSelectAll", "totalRows"];
import I18n from 'I18n';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UITable from 'UIComponents/table/UITable';
import styled, { theme } from 'academy-common-lib/utils/Styling';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TableContext = /*#__PURE__*/createContext({
  selectedRows: [],
  totalRows: 0,
  toggleRowSelection: () => {},
  toggleSelectAll: () => {},
  deselectAllRows: () => {}
});
export const TableProvider = ({
  children,
  ids
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const toggleSelectAll = useCallback(() => {
    const selectAll = selectedRows.length === 0;
    setSelectedRows(selectAll ? ids : []);
  }, [ids, selectedRows.length]);
  const deselectAllRows = useCallback(() => {
    setSelectedRows([]);
  }, []);
  const toggleRowSelection = useCallback(id => {
    setSelectedRows(rows => rows.includes(id) ? rows.filter(value => value !== id) : [...rows, id]);
  }, []);
  const contextState = useMemo(() => ({
    selectedRows,
    totalRows: ids.length,
    deselectAllRows,
    toggleSelectAll,
    toggleRowSelection
  }), [selectedRows, ids.length, deselectAllRows, toggleSelectAll, toggleRowSelection]);
  return /*#__PURE__*/_jsx(TableContext.Provider, {
    value: contextState,
    children: children
  });
};
const CHECKBOX_COLUMN_WIDTH = 60;
const StyledTable = styled(UITable).attrs({
  condensed: true
}).withConfig({
  displayName: "Table__StyledTable",
  componentId: "sc-1ji2cwi-0"
})(["background:", ";"], theme.colors.GYPSUM);
const StyledTableHeader = styled.tr.withConfig({
  displayName: "Table__StyledTableHeader",
  componentId: "sc-1ji2cwi-1"
})(["position:relative;th:not(.fixed-header){text-transform:none !important;visibility:", ";}"], ({
  $showActions
}) => $showActions ? 'hidden' : 'visible');
const HeaderActions = styled.tr.withConfig({
  displayName: "Table__HeaderActions",
  componentId: "sc-1ji2cwi-2"
})(["position:absolute;padding:0;background:none;padding-left:24px;height:100%;"]);
const TableHeader = ({
  renderHeader,
  renderHeaderActions
}) => {
  const _useContext = useContext(TableContext),
    {
      selectedRows,
      toggleSelectAll,
      totalRows
    } = _useContext,
    rest = _objectWithoutPropertiesLoose(_useContext, _excluded);
  const showActions = selectedRows.length > 0;
  return /*#__PURE__*/_jsxs(StyledTableHeader, {
    $showActions: showActions ? 1 : 0,
    children: [renderHeaderActions && /*#__PURE__*/_jsx("th", {
      className: "fixed-header",
      children: /*#__PURE__*/_jsx(UICheckbox, {
        "aria-label": I18n.text('learningCenter.table.checkboxAllTip'),
        checked: selectedRows.length === totalRows,
        indeterminate: selectedRows.length > 0,
        onChange: toggleSelectAll
      })
    }), /*#__PURE__*/_jsx(HeaderActions, {
      className: "fixed-header",
      children: /*#__PURE__*/_jsx(UIFlex, {
        align: "center",
        style: {
          height: '100%'
        },
        children: showActions && renderHeaderActions && renderHeaderActions(Object.assign({
          selectedRows,
          toggleSelectAll,
          totalRows
        }, rest))
      })
    }), renderHeader()]
  });
};
const TableRow = ({
  rowId,
  row,
  renderRow,
  hasActions
}) => {
  const {
    selectedRows,
    toggleRowSelection
  } = useContext(TableContext);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasActions && /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(UICheckbox, {
        "aria-label": I18n.text('learningCenter.table.checkboxTip'),
        checked: selectedRows.includes(rowId),
        onChange: () => toggleRowSelection(rowId)
      })
    }), /*#__PURE__*/_jsx("td", {
      style: {
        padding: 0
      }
    }), renderRow(row)]
  });
};
const Table = ({
  rows,
  renderHeader,
  renderHeaderActions,
  renderRow
}) => {
  const ids = Array.from(rows.keys());
  const hasActions = !!renderHeaderActions;
  return /*#__PURE__*/_jsx(TableProvider, {
    ids: ids,
    children: /*#__PURE__*/_jsxs(StyledTable, {
      children: [hasActions && /*#__PURE__*/_jsx("colgroup", {
        children: /*#__PURE__*/_jsx("col", {
          style: {
            width: CHECKBOX_COLUMN_WIDTH
          }
        })
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx(TableHeader, {
          renderHeader: renderHeader,
          renderHeaderActions: renderHeaderActions,
          rows: rows
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: ids.map(id => /*#__PURE__*/_jsx("tr", {
          "data-test-id": `row_${id}`,
          style: {
            background: theme.colors.OLAF
          },
          children: /*#__PURE__*/_jsx(TableRow, {
            hasActions: hasActions,
            renderRow: renderRow,
            row: rows.get(id),
            rowId: id
          })
        }, id))
      })]
    })
  });
};
export default Table;